import { FC, Suspense, lazy, useEffect, useState } from "react";

import { Route, Routes, useLocation } from "react-router-dom";

import {
  ADMIN_MAIN_URL,
  CHART_URL,
  DASHBOARD_URL,
  HOME_MAIN_URL,
  LOGIN_MAIN_URL,
  LOGIN_QR_URL,
  LOGIN_SIGNUP_URL,
  MYPAGE_MAIN_URL,
  REDIRECT_URI,
  RESERVATION_MAIN_URL,
  VIEW_URI,
  YOUTH_URL,
} from "@/constants/routes";
import useInactivityLogout from "@/hooks/useInactivityLogout";
import useViewMode, { useViewModeStore } from "@/hooks/useViewMode";
import Admin from "@/pages/Admin";
import ChartPage from "@/pages/Chart";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import KakaoLogin from "@/pages/Login/KakaoLogin";
import LoginMain from "@/pages/Login/LoginMain";
import LoginSignUp from "@/pages/Login/LoginSignUp";
import LoginWait from "@/pages/Login/LoginWait";
import MyPage from "@/pages/MyPage";
import ReservationPage from "@/pages/Reservation";
import View from "@/pages/View";

const YouthApp = lazy(() => import("@/pages/Youth"));

//type RouterProps = {};

const VIEW_MODE_LABELS: Record<string, string> = {
  user: "일반 유저 시점으로 보고 있습니다",
  unapproved: "미승인 유저 시점으로 보고 있습니다",
};

const ViewModeOverlay = () => {
  const { isRealAdmin, viewMode } = useViewMode();
  const { setViewMode } = useViewModeStore();
  const [phase, setPhase] = useState<"hidden" | "banner" | "border">("hidden");
  const isActive = viewMode !== "admin";

  useEffect(() => {
    if (isRealAdmin && isActive) {
      setPhase("banner");
      const timer = setTimeout(() => {
        setPhase("border");
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setPhase("hidden");
    }
  }, [isRealAdmin, isActive, viewMode]);

  if (phase === "hidden") return null;

  return (
    <>
      {/* Border - 배너 단계에서는 투명, border 단계에서 서서히 나타남 */}
      <div
        className="pointer-events-none fixed inset-0 z-[9998] rounded-sm transition-all duration-700 ease-in-out"
        style={{
          border:
            phase === "border"
              ? "8px solid rgba(251, 191, 36, 0.5)"
              : "8px solid transparent",
        }}
      />

      {/* 상단 배너 - 배너 단계에서 보이고, border 단계에서 위로 사라짐 */}
      <div
        className="fixed left-0 right-0 z-[9999] flex items-center justify-center gap-3 px-4 py-2 text-xs font-medium text-amber-900 shadow-md transition-all duration-700 ease-in-out"
        style={{
          top: phase === "banner" ? "0px" : "-48px",
          opacity: phase === "banner" ? 1 : 0,
          background: "rgba(251, 191, 36, 0.9)",
        }}
      >
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-700" />
          {VIEW_MODE_LABELS[viewMode] ?? ""}
        </span>
        <button
          type="button"
          onClick={() => setViewMode("admin")}
          className="rounded-md bg-amber-900/20 px-2.5 py-0.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-900/30"
        >
          관리자 뷰로 돌아가기
        </button>
      </div>

      {/* border 단계에서 우측 하단에 작은 복귀 버튼 */}
      <div
        className="fixed bottom-4 right-4 z-[9999] transition-all duration-500 ease-in-out"
        style={{
          opacity: phase === "border" ? 1 : 0,
          transform: phase === "border" ? "translateY(0)" : "translateY(12px)",
          pointerEvents: phase === "border" ? "auto" : "none",
        }}
      >
        <button
          type="button"
          onClick={() => setViewMode("admin")}
          className="flex items-center gap-1.5 rounded-full bg-amber-400/90 px-3 py-1.5 text-xs font-medium text-amber-900 shadow-lg backdrop-blur transition hover:bg-amber-400"
        >
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-amber-700" />
          관리자 뷰로 돌아가기
        </button>
      </div>
    </>
  );
};

const Router: FC = () => {
  const location = useLocation();
  useInactivityLogout();

  return (
    <>
      <ViewModeOverlay />
      <Routes location={location}>
        <Route path="" element={<LoginMain />} />
        <Route path={LOGIN_MAIN_URL} element={<LoginMain />} />
        <Route path={LOGIN_SIGNUP_URL} element={<LoginSignUp />} />
        <Route path={LOGIN_QR_URL} element={<LoginWait />} />
        <Route path={RESERVATION_MAIN_URL} element={<ReservationPage />} />
        <Route path={HOME_MAIN_URL} element={<Home />} />
        <Route path="/home/main" element={<Home />} />
        <Route path={VIEW_URI} element={<View />} />
        <Route path={MYPAGE_MAIN_URL} element={<MyPage />} />
        <Route path={REDIRECT_URI} element={<KakaoLogin />} />
        <Route path={DASHBOARD_URL} element={<Dashboard />} />
        <Route path={CHART_URL} element={<ChartPage />} />
        <Route path={ADMIN_MAIN_URL} element={<Admin />} />
        <Route
          path={`${YOUTH_URL}/*`}
          element={
            <Suspense fallback={null}>
              <YouthApp />
            </Suspense>
          }
        />
      </Routes>
    </>
  );
};

export default Router;
